import Constants from '@/Constants';
import { CardMode } from '@/models/CardMode';
import { CourseEnrollment } from '@/models/CourseEnrollment';
import { EntityType } from '@/models/EntityTypes';
import List from '@/models/List';
import { analyticsService, FlowEvents } from '@/services/AnalyticsService';
import { getValidUrl } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CourseSelection from '../CourseSelection';
import FavoriteSection from '../FavoriteSection';
import PersonImage from '../PersonImage';
import './style.scss';

interface Props {
    course: List;
    totalRating: number | string;
    mode: CardMode;
    customClass?: string;
    hasCurrentContent?: boolean;
    courseEnrollment?: CourseEnrollment;
}

const CourseCard: React.FC<Props> = (props: Props) => {
    const history = useHistory();

    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { course, totalRating, mode, hasCurrentContent, courseEnrollment } = props;

    const { personId } = useSelector((state: ApplicationState) => ({
        personId: state.identity.data.person.id,
    }));

    const [percentCompletedCourses, setPercentCompletedCourses] = useState<string>('0');

    const canFavorite = mode === 'view' && course.ownerId !== personId;
    const showViewersCount = course.ownerId === personId && !!course.views && course.views > 0;

    useEffect(() => {
        if (courseEnrollment) {
            setPercentCompletedCourses(`${courseEnrollment.progressPercentage}%`);
        }
    }, [courseEnrollment]);

    const navigateToProfile = (e: SyntheticEvent) => {
        if (course.ownerId) {
            e.preventDefault();
            analyticsService.logEvent(FlowEvents.ACCESS_AUTHOR_PROFILE);
            history.push(`/profile/${course.ownerId}/info`);
        }
    };

    const getLinkProfileAuthor = (
        course: List,
        children: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>,
    ) => {
        if (course.ownerId) {
            return (
                <a href="#" onClick={(e) => navigateToProfile(e)}>
                    {children}
                </a>
            );
        }

        return children;
    };

    return (
        <div className="main">
            <div className={` card-list ${mode === 'selection' && hasCurrentContent ? 'card-list__selected-list ' : ''}`}>
                <div className="card-list__thumb">
                    <div className="card-list__thumb-img" style={{ backgroundImage: `url('${getValidUrl(course.mainImage)}')` }} />
                    <div className="card-list__overlay" />
                    {showViewersCount && (
                        <div className="card-list__view ">
                            <div className="card-list__boxItens">
                                <i className="material-icons-outlined">{Constants.icons.VISIBILITY}</i>
                                <span>{course.views}</span>
                            </div>
                        </div>
                    )}

                    {canFavorite && (
                        <FavoriteSection
                            buttonMode
                            customActiveClass="active"
                            customClass={`card-list__like`}
                            entityId={course.id as number}
                            entityName={course.name}
                            entityType={EntityType.COURSE}
                            moduleId={course.moduleId as number}
                        />
                    )}
                    {mode === 'selection' && !hasCurrentContent && (
                        <CourseSelection course={course} customActiveClass="active" customClass={`card-list__like`} />
                    )}
                </div>
                <div className="card-list__body">
                    <div className="card-list__container-flag">
                        <div className="card-list__label">{strings.LIST}</div>
                        {course.ownerId === personId && (
                            <i className="material-icons icon-visibility">{course?.visibility ? Constants.icons.PUBLIC : Constants.icons.LOCK}</i>
                        )}
                    </div>
                    <div className="card-list__title" title={course.name}>
                        {course.name}
                    </div>
                    <div className="card-list__author-container" onClick={(e) => navigateToProfile(e)}>
                        {course.ownerId && course.author && <PersonImage alt={course.author} size={18} src={course.authorAvatar} />}
                        {getLinkProfileAuthor(
                            course,
                            <span className={course.ownerId ? 'card-list__author' : undefined} title={course.author}>
                                {course.author}
                            </span>,
                        )}
                    </div>
                    <div className="card-list__footer">
                        <div className="card-list__item-length">
                            {course.lessons ? course.lessons.length : 0}{' '}
                            {course.lessons && course.lessons.length === 1 ? strings.ITEM.toLowerCase() : strings.ITEMS.toLowerCase()}
                        </div>
                        <div className="card-list__duration">{`${course.workload || 0} ${strings.MINUTES}`}</div>
                        {mode === 'view' && (
                            <div className="card-list__rating">
                                <i className="material-icons">grade</i>
                                {totalRating}
                            </div>
                        )}
                    </div>
                    {percentCompletedCourses && (
                        <div className="card-list__progressBarContainer">
                            <div
                                className="card-program__progressBarContainer__progressBar"
                                style={{
                                    width: percentCompletedCourses,
                                }}
                            ></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CourseCard;
